import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Columns3, Pin, PinOff } from "lucide-react";

/**
 * Column-visibility popover.
 * Also hosts the "Freeze first column" switch.
 */
export default function ColumnsPopover({
  allColumns, hiddenCols, onToggleCol, onShowAll, onCompact,
  freezeFirst, onFreezeChange, numericColumns,
}) {
  const hiddenCount = Object.values(hiddenCols).filter(Boolean).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-8 gap-1.5 rounded-md text-[12px]" data-testid="rep-cols">
          <Columns3 className="h-3.5 w-3.5" /> Columns
          {hiddenCount > 0 && (
            <span className="bg-red-600 text-white text-[9px] font-mono-fin px-1 rounded">
              {hiddenCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-0" aria-describedby={undefined}>
        <div className="p-3 border-b border-slate-100 flex items-center justify-between gap-2">
          <div className="text-[11px] font-semibold text-slate-700">Show columns</div>
          <div className="flex gap-1">
            <button onClick={onShowAll} className="text-[10px] text-red-600 hover:underline" data-testid="cols-showall">All</button>
            <span className="text-slate-300">·</span>
            <button onClick={onCompact} className="text-[10px] text-slate-600 hover:underline" data-testid="cols-compact">Compact</button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-2 space-y-1 thin-scroll">
          {allColumns.map((c) => (
            <label key={c} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
              <Checkbox
                checked={!hiddenCols[c]}
                onCheckedChange={() => onToggleCol(c)}
                data-testid={`col-toggle-${c}`}
              />
              <span className="text-[12px] text-slate-700 flex-1 truncate">{c}</span>
              {numericColumns.includes(c) && <span className="text-[9px] text-slate-400 font-mono-fin">Σ</span>}
            </label>
          ))}
        </div>
        <div className="p-3 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {freezeFirst ? <Pin className="h-3.5 w-3.5 text-red-600" /> : <PinOff className="h-3.5 w-3.5 text-slate-400" />}
            <div>
              <div className="text-[11px] font-medium text-slate-700">Freeze first column</div>
              <div className="text-[9px] text-slate-500">Keeps it visible while scrolling</div>
            </div>
          </div>
          <Switch checked={freezeFirst} onCheckedChange={onFreezeChange} data-testid="rep-freeze" />
        </div>
      </PopoverContent>
    </Popover>
  );
}
